// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import Button, { ButtonProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectProps } from "@insite/mobius/Select";
import Tag, { TagProps, TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import AccordionSection from "@store/MobiusOverrides/AccordionSection/AccordionSection";
import { tableFilterProps } from "@store/Themes/common/props/table";
import React, { useEffect, useState } from "react";
import { useFastenersTableApiParameter } from "@store/Framework/Store/Store/Components/FastenersTable/FastenersTableHooks";
import updateSearchFields from "@store/Framework/Store/Store/Components/FastenersTable/Handlers/UpdateSearchFields";
import {
    useBoxCountCollection,
    useHeadDiameterCollection,
    useDriveTypeCollection,
    useFinishCollection,
    useHeadCollection,
    useLengthCollection,
    usePointCollection,
    useThreadCollection,
} from "@store/Framework/Store/Store/Data/Fasteners/FastenersHooks";

export interface ProductFastenersFilterProps {
    productId: string;
}

export interface ProductFastenersFilterStyles {
    container?: GridContainerProps;
    wrapper?: InjectableCss;
    clearFiltersButton?: ButtonProps;
    accordion?: AccordionPresentationProps;
    tagWrapper?: TagWrapperProps;
    appliedFilterTag?: TagPresentationProps;
    select?: SelectProps;
    filterGridItem?: GridItemProps;
}

const gridItemProps: GridItemProps = { width: [12, 3, 3, 2, 2] };

export const productFastenersFilterStyles: ProductFastenersFilterStyles = {
    wrapper: tableFilterProps.wrapper,
    accordion: tableFilterProps.accordion,
    container: tableFilterProps.formGridContainer,
    clearFiltersButton: tableFilterProps.clearButton,
    tagWrapper: tableFilterProps.tagWrapper,
    filterGridItem: gridItemProps,
};

const styles = productFastenersFilterStyles;

const ProductFastenersFilter = ({ productId }: ProductFastenersFilterProps) => {
    const dispatch = useAppDispatch();
    const fastenersTableApiParameter = useFastenersTableApiParameter();
    const lengthCollection = useLengthCollection();
    const boxCountCollection = useBoxCountCollection();
    const headDiameterCollection = useHeadDiameterCollection();
    const threadCollection = useThreadCollection();
    const driveTypeCollection = useDriveTypeCollection();
    const pointCollection = usePointCollection();
    const headCollection = useHeadCollection();
    const finishCollection = useFinishCollection();
    const [isOpen, setIsOpen] = useState(false);
    const [length, setLength] = useState("");
    const [boxCount, setBoxCount] = useState("");
    const [headDiameter, setHeadDiameter] = useState("");
    const [thread, setThread] = useState("");
    const [driveType, setDriveType] = useState("");
    const [point, setPoint] = useState("");
    const [head, setHead] = useState("");
    const [finish, setFinish] = useState("");

    useEffect(() => {
        setLength(fastenersTableApiParameter.length ?? "");
        setBoxCount(fastenersTableApiParameter.boxCount ?? "");
        setHeadDiameter(fastenersTableApiParameter.headDiameter ?? "");
        setThread(fastenersTableApiParameter.thread ?? "");
        setDriveType(fastenersTableApiParameter.driveType ?? "");
        setPoint(fastenersTableApiParameter.point ?? "");
        setHead(fastenersTableApiParameter.head ?? "");
        setFinish(fastenersTableApiParameter.finish ?? "");
    }, [
        fastenersTableApiParameter.length,
        fastenersTableApiParameter.boxCount,
        fastenersTableApiParameter.headDiameter,
        fastenersTableApiParameter.thread,
        fastenersTableApiParameter.driveType,
        fastenersTableApiParameter.point,
        fastenersTableApiParameter.head,
        fastenersTableApiParameter.finish,
    ]);

    const dispatchFilterUpdate = (updatedFilters: {
        length?: string;
        boxCount?: string;
        headDiameter?: string;
        thread?: string;
        driveType?: string;
        point?: string;
        head?: string;
        finish?: string;
    }) => {
        if (!productId) {
            return;
        }

        dispatch(
            updateSearchFields({
                productId,
                page: 1,
                ...updatedFilters,
            }),
        );
    };

    const lengthChangeHandler = (value: string) => {
        setLength(value);
        dispatchFilterUpdate({ length: value });
    };

    const boxCountChangeHandler = (value: string) => {
        setBoxCount(value);
        dispatchFilterUpdate({ boxCount: value });
    };

    const headDiameterChangeHandler = (value: string) => {
        setHeadDiameter(value);
        dispatchFilterUpdate({ headDiameter: value });
    };

    const threadChangeHandler = (value: string) => {
        setThread(value);
        dispatchFilterUpdate({ thread: value });
    };

    const driveTypeChangeHandler = (value: string) => {
        setDriveType(value);
        dispatchFilterUpdate({ driveType: value });
    };

    const pointChangeHandler = (value: string) => {
        setPoint(value);
        dispatchFilterUpdate({ point: value });
    };

    const headChangeHandler = (value: string) => {
        setHead(value);
        dispatchFilterUpdate({ head: value });
    };

    const finishChangeHandler = (value: string) => {
        setFinish(value);
        dispatchFilterUpdate({ finish: value });
    };

    const clearFiltersClickHandler = () => {
        setLength("");
        setBoxCount("");
        setHeadDiameter("");
        setThread("");
        setDriveType("");
        setPoint("");
        setHead("");
        setFinish("");

        dispatchFilterUpdate({
            length: "",
            boxCount: "",
            headDiameter: "",
            thread: "",
            driveType: "",
            point: "",
            head: "",
            finish: "",
        });
    };

    const facets = (
        <TagWrapper {...styles.tagWrapper}>
            {length && (
                <Tag {...styles.appliedFilterTag} onDelete={() => lengthChangeHandler("")}>
                    {`${translate("{0}:", translate("Length"))} ${length}`}
                </Tag>
            )}
            {boxCount && (
                <Tag {...styles.appliedFilterTag} onDelete={() => boxCountChangeHandler("")}>
                    {`${translate("{0}:", translate("Box Count"))} ${boxCount}`}
                </Tag>
            )}
            {headDiameter && (
                <Tag {...styles.appliedFilterTag} onDelete={() => headDiameterChangeHandler("")}>
                    {`${translate("{0}:", translate("Diameter"))} ${headDiameter}`}
                </Tag>
            )}
            {thread && (
                <Tag {...styles.appliedFilterTag} onDelete={() => threadChangeHandler("")}>
                    {`${translate("{0}:", translate("Thread"))} ${thread}`}
                </Tag>
            )}
            {driveType && (
                <Tag {...styles.appliedFilterTag} onDelete={() => driveTypeChangeHandler("")}>
                    {`${translate("{0}:", translate("Drive Type"))} ${driveType}`}
                </Tag>
            )}
            {point && (
                <Tag {...styles.appliedFilterTag} onDelete={() => pointChangeHandler("")}>
                    {`${translate("{0}:", translate("Point"))} ${point}`}
                </Tag>
            )}
            {head && (
                <Tag {...styles.appliedFilterTag} onDelete={() => headChangeHandler("")}>
                    {`${translate("{0}:", translate("Head"))} ${head}`}
                </Tag>
            )}
            {finish && (
                <Tag {...styles.appliedFilterTag} onDelete={() => finishChangeHandler("")}>
                    {`${translate("{0}:", translate("Finish"))} ${finish}`}
                </Tag>
            )}
        </TagWrapper>
    );

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="fasteners_filter">
            <Accordion {...styles.accordion} headingLevel={2}>
                <AccordionSection
                    data-test-selector="fasteners_filterHeader"
                    title={translate("Filters")}
                    expanded={isOpen}
                    onTogglePanel={() => setIsOpen(!isOpen)}
                >
                    <GridContainer {...styles.container}>
                        {lengthCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_Length">
                                <Select
                                    label={translate("Length")}
                                    {...styles.select}
                                    value={length}
                                    onChange={event => lengthChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {lengthCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {boxCountCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_BoxCount">
                                <Select
                                    label={translate("Box Count")}
                                    {...styles.select}
                                    value={boxCount}
                                    onChange={event => boxCountChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {boxCountCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {headDiameterCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_HeadDiameter">
                                <Select
                                    label={translate("Head Diameter")}
                                    {...styles.select}
                                    value={headDiameter}
                                    onChange={event => headDiameterChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {headDiameterCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {threadCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_Thread">
                                <Select
                                    label={translate("Thread")}
                                    {...styles.select}
                                    value={thread}
                                    onChange={event => threadChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {threadCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {driveTypeCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_DriveType">
                                <Select
                                    label={translate("Drive Type")}
                                    {...styles.select}
                                    value={driveType}
                                    onChange={event => driveTypeChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {driveTypeCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {pointCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_Point">
                                <Select
                                    label={translate("Point")}
                                    {...styles.select}
                                    value={point}
                                    onChange={event => pointChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {pointCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {headCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_Head">
                                <Select
                                    label={translate("Head")}
                                    {...styles.select}
                                    value={head}
                                    onChange={event => headChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {headCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {headCollection.length > 0 && (
                            <GridItem {...styles.filterGridItem} data-test-selector="fasteners_Finish">
                                <Select
                                    label={translate("Finish")}
                                    {...styles.select}
                                    value={finish}
                                    onChange={event => finishChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {finishCollection.map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                    </GridContainer>
                    <FormActions>
                        <Button
                            {...styles.clearFiltersButton}
                            onClick={clearFiltersClickHandler}
                            data-test-selector="fasteners_clearFiltersButton"
                        >
                            {translate("Clear")}
                            <VisuallyHidden>{translate("All Filters")}</VisuallyHidden>
                        </Button>
                    </FormActions>
                </AccordionSection>
            </Accordion>
            {facets}
        </StyledWrapper>
    );
};

export default ProductFastenersFilter;
